import { useQuery, useQueryClient } from '@tanstack/react-query';
import { domainsApi } from '@/api/domains';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { DnsRecordRequest, RedirectRequest } from '@/types/api';

/** Read from 2d2s only while the tab is open: each read is several calls to it. */
export function useSiteDns(siteId: string, enabled: boolean) {
  return useQuery({ queryKey: queryKeys.siteDns(siteId), queryFn: () => domainsApi.view(siteId), enabled, staleTime: 30_000 });
}

/** Everything the tab read from 2d2s, read again: the zones and the sections that read on their own. */
export function useRefreshDns(siteId: string) {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.siteDns(siteId) });
}

/** Everything that changes a domain, each refreshing what the tab shows. */
function useDnsChange<TVariables, TData>(
  siteId: string,
  change: (variables: TVariables) => Promise<TData>,
  callbacks?: MutationCallbacks<TData, TVariables>,
) {
  return useApiMutation(change, () => [queryKeys.siteDns(siteId)], callbacks);
}

export type RecordChange =
  | { readonly kind: 'create'; readonly domainId: string; readonly record: DnsRecordRequest }
  | { readonly kind: 'update'; readonly domainId: string; readonly recordId: string; readonly record: DnsRecordRequest }
  | { readonly kind: 'delete'; readonly domainId: string; readonly recordId: string };

export function useRecordChange(siteId: string, callbacks?: MutationCallbacks<unknown, RecordChange>) {
  return useDnsChange(
    siteId,
    (change: RecordChange) =>
      change.kind === 'create'
        ? domainsApi.createRecord(siteId, change.domainId, change.record)
        : change.kind === 'update'
          ? domainsApi.updateRecord(siteId, change.domainId, change.recordId, change.record)
          : domainsApi.deleteRecord(siteId, change.domainId, change.recordId),
    callbacks,
  );
}

export function useSetSslMode(siteId: string, domainId: string, callbacks?: MutationCallbacks<{ sslMode: string }, string>) {
  return useDnsChange(siteId, (mode: string) => domainsApi.setSslMode(siteId, domainId, mode), callbacks);
}

export function useSetAlwaysHttps(siteId: string, domainId: string, callbacks?: MutationCallbacks<{ alwaysUseHttps: boolean }, boolean>) {
  return useDnsChange(siteId, (enabled: boolean) => domainsApi.setAlwaysHttps(siteId, domainId, enabled), callbacks);
}

/** How a domain's name servers changed: read when the NS section opens. */
export function useNameServerHistory(siteId: string, domainId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.siteNameServers(siteId, domainId),
    queryFn: () => domainsApi.nameServers(siteId, domainId),
    enabled,
    staleTime: 60_000,
  });
}

/** A domain's Cloudflare Bulk Redirects: read when the section opens. */
export function useRedirects(siteId: string, domainId: string, enabled: boolean) {
  return useQuery({ queryKey: queryKeys.siteRedirects(siteId, domainId), queryFn: () => domainsApi.redirects(siteId, domainId), enabled, staleTime: 30_000 });
}

export type RedirectChange =
  | { readonly kind: 'create'; readonly redirect: RedirectRequest }
  | { readonly kind: 'update'; readonly redirectId: string; readonly redirect: RedirectRequest }
  | { readonly kind: 'delete'; readonly redirectId: string };

export function useRedirectChange(siteId: string, domainId: string, callbacks?: MutationCallbacks<unknown, RedirectChange>) {
  return useApiMutation(
    (change: RedirectChange) =>
      change.kind === 'create'
        ? domainsApi.createRedirect(siteId, domainId, change.redirect)
        : change.kind === 'update'
          ? domainsApi.updateRedirect(siteId, domainId, change.redirectId, change.redirect)
          : domainsApi.deleteRedirect(siteId, domainId, change.redirectId),
    () => [queryKeys.siteRedirects(siteId, domainId)],
    callbacks,
  );
}

export function usePurgeCache(siteId: string, domainId: string, callbacks?: MutationCallbacks<{ purged: boolean }, void>) {
  return useDnsChange(siteId, () => domainsApi.purgeCache(siteId, domainId), callbacks);
}
